import { useForm } from '@refinedev/antd';
import { Create } from '@refinedev/antd';
import { Form, Input, InputNumber, Card, Row, Col, DatePicker, Select, Spin } from 'antd';
import { useState, useEffect } from 'react';
import { apiClient, tokenManager } from '../../lib/api-client';

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
}

interface City {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  regionId: number;
}

interface BusCompany {
  id: number;
  name: string;
}

interface BusStation {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  address?: string;
  cityId: number;
}

interface BusLayout {
  id: number;
  name: string;
  totalSeats: number;
  description?: string;
}

export const BusTripCreate = () => {
  const [form] = Form.useForm();
  const { formProps, saveButtonProps } = useForm({
    resource: 'admin/bus/trips',
    redirect: 'list',
  });

  // Check if current user is a bus company
  const user = tokenManager.getUser();
  const isBusCompany = user?.userType === 'bus_company';
  const busCompanyId = user?.busCompanyId;

  // Store original onFinish from formProps
  const originalOnFinish = formProps.onFinish;

  // Handle form submission with date conversion
  const handleFormSubmit = async (values: Record<string, unknown>) => {
    console.log('Form submitted with values:', values);

    // Convert dayjs objects to ISO strings for the backend
    const formattedValues = {
      ...values,
      departureTime: values.departureTime
        ? (values.departureTime as { toISOString: () => string }).toISOString()
        : undefined,
      arrivalTime: values.arrivalTime
        ? (values.arrivalTime as { toISOString: () => string }).toISOString()
        : undefined,
    };

    console.log('Formatted values:', formattedValues);

    // Call the original onFinish with formatted values
    if (originalOnFinish) {
      return originalOnFinish(formattedValues);
    }
  };

  // Custom save button props that manually submits our form
  const customSaveButtonProps = {
    ...saveButtonProps,
    onClick: () => {
      console.log('Save button clicked');
      form.submit();
    },
  };

  const [regions, setRegions] = useState<Region[]>([]);
  const [companies, setCompanies] = useState<BusCompany[]>([]);
  const [layouts, setLayouts] = useState<BusLayout[]>([]);
  const [departureCities, setDepartureCities] = useState<City[]>([]);
  const [arrivalCities, setArrivalCities] = useState<City[]>([]);
  const [departureStations, setDepartureStations] = useState<BusStation[]>([]);
  const [arrivalStations, setArrivalStations] = useState<BusStation[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingLayouts, setLoadingLayouts] = useState(true);
  const [loadingDepartureCities, setLoadingDepartureCities] = useState(false);
  const [loadingArrivalCities, setLoadingArrivalCities] = useState(false);
  const [loadingDepartureStations, setLoadingDepartureStations] = useState(false);
  const [loadingArrivalStations, setLoadingArrivalStations] = useState(false);

  // Fetch regions on mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await apiClient.get('/admin/regions');
        setRegions(response.data);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, []);

  // Fetch companies on mount (skip for bus company users)
  useEffect(() => {
    if (isBusCompany) {
      // Auto-set busCompanyId for bus company users
      form.setFieldsValue({ busCompanyId });
      setLoadingCompanies(false);
      return;
    }
    const fetchCompanies = async () => {
      try {
        const response = await apiClient.get('/admin/bus/companies');
        setCompanies(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch bus layouts on mount
  useEffect(() => {
    const fetchLayouts = async () => {
      try {
        const response = await apiClient.get('/admin/bus/layouts');
        // Filter only active layouts
        const activeLayouts = (response.data || []).filter((l: BusLayout & { isActive?: boolean }) => l.isActive !== false);
        setLayouts(activeLayouts);
      } catch (error) {
        console.error('Failed to fetch layouts:', error);
      } finally {
        setLoadingLayouts(false);
      }
    };
    fetchLayouts();
  }, []);

  // Handle layout selection - auto-fill totalSeats
  const handleLayoutChange = (layoutId: number | undefined) => {
    if (layoutId) {
      const selectedLayout = layouts.find(l => l.id === layoutId);
      if (selectedLayout) {
        form.setFieldsValue({ totalSeats: selectedLayout.totalSeats });
      }
    }
  };

  // Fetch cities when departure region changes
  const handleDepartureRegionChange = async (regionId: number) => {
    form.setFieldsValue({ departureCityId: undefined, departureBusStationId: undefined });
    setDepartureCities([]);
    setDepartureStations([]);
    if (regionId) {
      setLoadingDepartureCities(true);
      try {
        const response = await apiClient.get(`/admin/cities?regionId=${regionId}`);
        setDepartureCities(response.data);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      } finally {
        setLoadingDepartureCities(false);
      }
    }
  };

  // Fetch cities when arrival region changes
  const handleArrivalRegionChange = async (regionId: number) => {
    form.setFieldsValue({ arrivalCityId: undefined, arrivalBusStationId: undefined });
    setArrivalCities([]);
    setArrivalStations([]);
    if (regionId) {
      setLoadingArrivalCities(true);
      try {
        const response = await apiClient.get(`/admin/cities?regionId=${regionId}`);
        setArrivalCities(response.data);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      } finally {
        setLoadingArrivalCities(false);
      }
    }
  };

  // Fetch bus stations when departure city changes
  const handleDepartureCityChange = async (cityId: number) => {
    form.setFieldsValue({ departureBusStationId: undefined });
    setDepartureStations([]);
    if (cityId) {
      setLoadingDepartureStations(true);
      try {
        const response = await apiClient.get(`/admin/bus/stations?cityId=${cityId}`);
        setDepartureStations(response.data);
      } catch (error) {
        console.error('Failed to fetch bus stations:', error);
      } finally {
        setLoadingDepartureStations(false);
      }
    }
  };

  // Fetch bus stations when arrival city changes
  const handleArrivalCityChange = async (cityId: number) => {
    form.setFieldsValue({ arrivalBusStationId: undefined });
    setArrivalStations([]);
    if (cityId) {
      setLoadingArrivalStations(true);
      try {
        const response = await apiClient.get(`/admin/bus/stations?cityId=${cityId}`);
        setArrivalStations(response.data);
      } catch (error) {
        console.error('Failed to fetch bus stations:', error);
      } finally {
        setLoadingArrivalStations(false);
      }
    }
  };

  return (
    <Create saveButtonProps={customSaveButtonProps} title="Yangi avtobus reysi">
      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card title="Asosiy ma'lumotlar">
              {isBusCompany ? (
                <Form.Item name="busCompanyId" hidden>
                  <Input type="hidden" />
                </Form.Item>
              ) : (
                <Form.Item
                  label="Avtobus kompaniyasi"
                  name="busCompanyId"
                  rules={[{ required: true, message: 'Kompaniyani tanlang' }]}
                >
                  <Select
                    placeholder="Kompaniyani tanlang"
                    showSearch
                    loading={loadingCompanies}
                    options={companies.map((c) => ({ value: c.id, label: c.name }))}
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                label="Avtobus turi"
                name="busType"
                rules={[{ required: true, message: 'Turni tanlang' }]}
                initialValue="STANDARD"
              >
                <Select
                  options={[
                    { value: 'STANDARD', label: 'Standart' },
                    { value: 'COMFORT', label: 'Komfort' },
                    { value: 'VIP', label: 'VIP' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                label="Avtobus sxemasi"
                name="busLayoutId"
                tooltip="O'rindiqlar joylashuvi sxemasini tanlang"
              >
                <Select
                  placeholder="Sxemani tanlang (ixtiyoriy)"
                  showSearch
                  allowClear
                  loading={loadingLayouts}
                  onChange={handleLayoutChange}
                  options={layouts.map((l) => ({
                    value: l.id,
                    label: `${l.name} (${l.totalSeats} o'rindiq)`,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item label="Avtobus raqami" name="busNumber">
                <Input placeholder="01A123AA" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Jami o'rindiqlar"
                    name="totalSeats"
                    rules={[{ required: true, message: "O'rindiqlar sonini kiriting" }]}
                    initialValue={45}
                  >
                    <InputNumber style={{ width: '100%' }} min={10} max={100} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Narx (so'm)"
                    name="pricePerSeat"
                    rules={[{ required: true, message: 'Narxni kiriting' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                      }
                      parser={(value) => value!.replace(/\s/g, '') as unknown as 0}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Qo'shimcha" style={{ marginTop: 24 }}>
              <Form.Item label="Tavsif" name="description">
                <Input.TextArea placeholder="Reys haqida qo'shimcha ma'lumot" rows={3} />
              </Form.Item>

              <Form.Item label="Qulayliklar" name="amenities">
                <Select
                  mode="multiple"
                  placeholder="Qulayliklarni tanlang"
                  options={[
                    { value: 'wifi', label: 'Wi-Fi' },
                    { value: 'ac', label: 'Konditsioner' },
                    { value: 'toilet', label: 'Hojatxona' },
                    { value: 'tv', label: 'TV' },
                    { value: 'usb', label: 'USB quvvatlash' },
                    { value: 'water', label: 'Bepul suv' },
                  ]}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Jo'nash joyi">
              <Form.Item
                label="Viloyat"
                name="departureRegionId"
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  loading={loadingRegions}
                  options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
                  onChange={handleDepartureRegionChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                label="Shahar"
                name="departureCityId"
                rules={[{ required: true, message: 'Shaharni tanlang' }]}
              >
                <Select
                  placeholder={departureCities.length === 0 ? "Avval viloyatni tanlang" : "Shaharni tanlang"}
                  showSearch
                  loading={loadingDepartureCities}
                  disabled={departureCities.length === 0}
                  options={departureCities.map((c) => ({ value: c.id, label: c.nameUz }))}
                  onChange={handleDepartureCityChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingDepartureCities ? <Spin size="small" /> : 'Shahar topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Avtovokzal"
                name="departureBusStationId"
                rules={[{ required: true, message: 'Avtovokzalni tanlang' }]}
              >
                <Select
                  placeholder={departureStations.length === 0 ? "Avval shaharni tanlang" : "Avtovokzalni tanlang"}
                  showSearch
                  loading={loadingDepartureStations}
                  disabled={departureStations.length === 0}
                  options={departureStations.map((s) => ({
                    value: s.id,
                    label: s.nameUz,
                    title: s.address,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingDepartureStations ? <Spin size="small" /> : 'Avtovokzal topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Jo'nash vaqti"
                name="departureTime"
                rules={[{ required: true, message: "Jo'nash vaqtini tanlang" }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Sana va vaqtni tanlang"
                />
              </Form.Item>
            </Card>

            <Card title="Yetib borish joyi" style={{ marginTop: 24 }}>
              <Form.Item
                label="Viloyat"
                name="arrivalRegionId"
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  loading={loadingRegions}
                  options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
                  onChange={handleArrivalRegionChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                label="Shahar"
                name="arrivalCityId"
                rules={[{ required: true, message: 'Shaharni tanlang' }]}
              >
                <Select
                  placeholder={arrivalCities.length === 0 ? "Avval viloyatni tanlang" : "Shaharni tanlang"}
                  showSearch
                  loading={loadingArrivalCities}
                  disabled={arrivalCities.length === 0}
                  options={arrivalCities.map((c) => ({ value: c.id, label: c.nameUz }))}
                  onChange={handleArrivalCityChange}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingArrivalCities ? <Spin size="small" /> : 'Shahar topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Avtovokzal"
                name="arrivalBusStationId"
                rules={[{ required: true, message: 'Avtovokzalni tanlang' }]}
              >
                <Select
                  placeholder={arrivalStations.length === 0 ? "Avval shaharni tanlang" : "Avtovokzalni tanlang"}
                  showSearch
                  loading={loadingArrivalStations}
                  disabled={arrivalStations.length === 0}
                  options={arrivalStations.map((s) => ({
                    value: s.id,
                    label: s.nameUz,
                    title: s.address,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={loadingArrivalStations ? <Spin size="small" /> : 'Avtovokzal topilmadi'}
                />
              </Form.Item>

              <Form.Item
                label="Yetib borish vaqti"
                name="arrivalTime"
                rules={[{ required: true, message: 'Yetib borish vaqtini tanlang' }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Sana va vaqtni tanlang"
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};
